import { useState } from 'react';
import type { ReactNode } from 'react';

import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import {
  alpha,
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import {
  AttachmentTile,
  ChannelBadge,
  DeliveryStatusIcon,
  describeDeliveryStatus,
  formatClock,
  MaskedPlaceholder,
  QuotedReplyStrip,
} from '@/shared/ui/conversation';
import { getIntlLocale } from '@/shared/i18n';

import { resolveAttachmentContentUrl } from '../../api/conversationsApi';
import { isPiiGatedAttachmentType } from '../../model/attachmentAccess';
import {
  effectiveText,
  isOperatorRow,
  operatorMetaLabel,
  quotedPreview,
  resolveMessageBodyKind,
  resolveWhoLabel,
} from '../../model/messageDisplay';
import type { ConversationMessageView } from '../../model/types';

export interface MessageBubbleProps {
  message: ConversationMessageView;
  canViewPii: boolean;
  canReply: boolean;
  /** Pre-resolved via `resolveCallbackLabel` in `Timeline` (needs the full ordered row list). */
  callbackLabel?: string | null;
  /** The quoted row, already resolved against the loaded pages — `undefined` renders nothing (§11.4). */
  quoted?: ConversationMessageView;
  onReplyToThis?: (message: ConversationMessageView) => void;
  onJumpToMessage?: (messageId: string) => void;
  /** The signed-in operator — decides the "you" variant of an operator row's who-label (design §13.2). */
  meId?: string | null;
}

/**
 * One timeline row — the meta line (channel · who · time · delivery) plus a body resolved per the
 * kind × direction table of design §7.4, with the delta's edited/deleted/masked/quoted treatment
 * (§11.4) and attachment tiles (§11.3). Shared across the inbox `Timeline` (this file's only caller
 * today); Customer 360 renders its OWN compact list instead (design §6 line 4), so this component is
 * feature-local rather than a `shared/ui` primitive.
 */
export function MessageBubble({
  message,
  canViewPii,
  canReply,
  callbackLabel,
  quoted,
  onReplyToThis,
  onJumpToMessage,
  meId,
}: MessageBubbleProps) {
  const { t } = useTranslation('conversations');
  const [showOriginal, setShowOriginal] = useState(false);
  const locale = getIntlLocale();
  const bodyKind = resolveMessageBodyKind(message, canViewPii);
  // A present `editedText` means the row was edited even when the wire's `edited` flag is false/null
  // (defensive — design §11.4) — matches `effectiveText()`, which already prefers `editedText`
  // unconditionally.
  const isEdited = Boolean(message.edited) || message.editedText != null;
  const isOutbound = message.direction === 'OUT';
  // `direction=OUT && (kind=OPERATOR || handledBy=OPERATOR)` (design §13.3) — a row written before the
  // hub's backfill is `kind=TEXT, handledBy=OPERATOR` and must render identically to a `kind=OPERATOR`
  // one (bronze surface, named who-label), not as a bot bubble.
  const isOperatorMessage = isOperatorRow(message);
  const whoLabel = isOperatorMessage
    ? operatorMetaLabel(message, meId, t)
    : { label: resolveWhoLabel(message, t, meId) };
  const isNotification = message.kind === 'NOTIFICATION';
  const isSystem = message.kind === 'SYSTEM';

  const deliveryDescription =
    isOutbound && !isSystem
      ? describeDeliveryStatus(
          message.deliveryStatus,
          message.deliveryReason,
          message.deliveryAttempts,
          message.channel,
          t,
        )
      : null;

  const metaTimeTooltip = t('timeline.messageIds', {
    id: message.id.slice(0, 8),
    channelIds: (message.channelMessageIds ?? []).join(', ') || '—',
  });

  const metaLine = (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
      <ChannelBadge channel={message.channel} size="small" variant="outlined" />
      {whoLabel.tooltip ? (
        <Tooltip title={whoLabel.tooltip}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {whoLabel.label}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {whoLabel.label}
        </Typography>
      )}
      <Tooltip title={metaTimeTooltip}>
        <Typography variant="caption" color="text.secondary">
          {formatClock(message.createdAt, locale)}
        </Typography>
      </Tooltip>
      {bodyKind === 'normal' && isEdited ? (
        <Stack direction="row" spacing={0.25} alignItems="center">
          <EditRoundedIcon sx={{ fontSize: 14 }} color="action" />
          <Typography variant="caption" color="text.secondary">
            {t('timeline.edited')}
          </Typography>
        </Stack>
      ) : null}
      {isOutbound && !isSystem ? (
        <DeliveryStatusIcon
          status={message.deliveryStatus}
          reason={message.deliveryReason}
          attempts={message.deliveryAttempts}
          channel={message.channel}
        />
      ) : null}
      {canReply && message.direction === 'IN' && bodyKind !== 'deleted' && onReplyToThis ? (
        <Tooltip title={t('timeline.replyToThis')}>
          <IconButton
            size="small"
            aria-label={t('timeline.replyToThis')}
            onClick={() => onReplyToThis(message)}
            sx={{ ml: 'auto' }}
          >
            <ReplyRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );

  const attachmentsNode =
    bodyKind !== 'deleted' && message.attachments && message.attachments.length > 0 ? (
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 0.75 }}>
        {message.attachments.map((attachment) => (
          <AttachmentTile
            key={attachment.attachmentId}
            type={attachment.type}
            mimeType={attachment.mimeType}
            sizeBytes={attachment.sizeBytes}
            fileName={attachment.fileName}
            durationSeconds={attachment.durationSeconds}
            storageStatus={attachment.storageStatus}
            resolvedUrl={resolveAttachmentContentUrl(attachment.contentUrl)}
            locked={isPiiGatedAttachmentType(attachment.type) && !canViewPii}
          />
        ))}
      </Stack>
    ) : null;

  function renderBody(): ReactNode {
    if (bodyKind === 'deleted') {
      return (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} color="disabled" />
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t(isOutbound ? 'timeline.deletedOutbound' : 'timeline.deleted')}
          </Typography>
        </Stack>
      );
    }
    if (bodyKind === 'masked') {
      return <MaskedPlaceholder kind={message.kind} />;
    }

    const text = effectiveText(message);

    switch (message.kind) {
      case 'COMMAND':
        return (
          <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {text}
          </Typography>
        );
      case 'CALLBACK':
        return callbackLabel ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TouchAppRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {t('timeline.callbackTapped', { label: callbackLabel })}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {text}
          </Typography>
        );
      case 'CONTACT':
        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ContactPhoneRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2">{text}</Typography>
          </Stack>
        );
      case 'MEDIA': {
        if (message.attachments && message.attachments.length > 0) {
          return null; // rendered by attachmentsNode below
        }
        const fallbackPart = message.parts?.find(
          (part) => part.type === 'IMAGE' || part.type === 'DOCUMENT',
        );
        if (fallbackPart) {
          return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {t(`timeline.media.${fallbackPart.type}`)}
            </Typography>
          );
        }
        return null;
      }
      case 'LOCATION':
        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <PlaceRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2">{t('timeline.location')}</Typography>
          </Stack>
        );
      case 'UNSUPPORTED':
        return (
          <Typography variant="body2" color="text.secondary">
            {t('timeline.unsupported')}
          </Typography>
        );
      case 'NOTIFICATION':
        return <Typography variant="body2">{text ?? t('timeline.campaign')}</Typography>;
      default:
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {text ?? (canViewPii ? t('timeline.textUnavailable') : '')}
          </Typography>
        );
    }
  }

  if (isSystem) {
    return (
      <Box sx={{ textAlign: 'center', my: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {message.text ?? t('timeline.systemEvent')}
        </Typography>
      </Box>
    );
  }

  const surfaceSx = isOperatorMessage
    ? {
        bgcolor: (theme: Theme) => alpha(theme.palette.secondary.main, 0.1),
        borderColor: 'secondary.light',
      }
    : isOutbound && !isNotification
      ? {
          bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
          borderColor: 'primary.light',
        }
      : message.kind === 'UNSUPPORTED'
        ? { borderStyle: 'dashed' }
        : { bgcolor: 'action.hover' };

  return (
    <Stack
      direction="row"
      justifyContent={isOutbound ? 'flex-end' : 'flex-start'}
      sx={{ width: '100%' }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          maxWidth: { xs: '100%', sm: '72%' },
          minWidth: 0,
          ...surfaceSx,
        }}
      >
        <Stack spacing={0.5}>
          {metaLine}
          {quoted ? (
            <QuotedReplyStrip
              who={resolveWhoLabel(quoted, t, meId)}
              time={formatClock(quoted.createdAt, locale)}
              preview={quotedPreview(quoted, canViewPii, t)}
              isOperatorQuote={isOperatorRow(quoted)}
              onJump={() => onJumpToMessage?.(quoted.id)}
            />
          ) : null}
          {renderBody()}
          {bodyKind === 'normal' && isEdited && message.editedText != null ? (
            <>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowOriginal((v) => !v)}
                aria-expanded={showOriginal}
                sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0.5 }}
              >
                {t(showOriginal ? 'timeline.hideOriginal' : 'timeline.showOriginal')}
              </Button>
              <Collapse in={showOriginal}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {t('timeline.original', { text: message.text ?? '' })}
                </Typography>
              </Collapse>
            </>
          ) : null}
          {attachmentsNode}
          {bodyKind === 'normal' && message.actions && message.actions.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 0.5 }}>
              {message.actions.map((action, index) => (
                <Button key={`${action.type}-${index}`} size="small" variant="outlined" disabled>
                  {action.label}
                </Button>
              ))}
            </Stack>
          ) : null}
          {isOutbound && !isSystem && deliveryDescription?.secondary ? (
            <Typography
              variant="caption"
              sx={{
                color: deliveryDescription.color,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                justifyContent: 'flex-end',
              }}
            >
              <deliveryDescription.Icon fontSize="inherit" />
              {deliveryDescription.secondary}
            </Typography>
          ) : null}
        </Stack>
      </Paper>
    </Stack>
  );
}
